<template>
  <component
    :is="comp"
    v-if="shouldDisplayUpgrade"
    class="rounded-lg body-1-bold fixed left-1/2 z-[1] flex w-max -translate-x-1/2 items-center p-12"
    :class="{
      'bg-static-default-hi': isLoading,
      'bg-action-default-hi text-onaction-default-hi hover:bg-action-default-hi-hover':
        canUpgrade,
      'bg-static-info-mid text-static-brand-hi cursor-default': isUpgraded,
      [isUpgradedLabelClassnames]: isUpgraded,
      [$style.animateShimmer]: isUpgraded,
      [$style.slideInFromTop]: applyAnimation,
    }"
    :style="topPosition"
    @click="handlePillClick"
  >
    <!-- This div is necessary to put the right color on the spinner -->
    <div v-if="isLoading" class="text-static-default-hi mr-4">
      <RevSpinner aria-hidden />
    </div>
    <IconCheckWithSparkles v-if="isUpgraded" class="mr-4" />
    <div
      :class="{
        [$style.animateRainbow]: isLoading,
        [isLoadingLabelClassnames]: isLoading,
      }"
    >
      {{ label }}
    </div>
    <IconChevronDown v-if="canUpgrade" />
  </component>
  <UpgradeModal
    :pickers
    :style="topPosition"
    @upgrade-accepted="handleUpgradeAccepted"
  />
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { tw } from '@backmarket/utils/string/tw'
import { openModal } from '@ds/components/ModalBase'
import { RevSpinner } from '@ds/components/Spinner'
import { IconCheckWithSparkles } from '@ds/icons/IconCheckWithSparkles'
import { IconChevronDown } from '@ds/icons/IconChevronDown'

import {
  CATEGORIES_WITH_UPGRADE,
  MODAL_NAMES,
} from '~/scopes/product/constants'

import { useShowStickyBarTop } from '../StickyBarTop/composables/useShowStickyBarTop'
import { useShowStickyBarTopSmall } from '../StickyBarTopSmall/composables/useShowStickyBarTopSmall'

import translations from './UpgradeForLess.translations'
import UpgradeModal from './components/UpgradeModal/UpgradeModal.vue'

const props = defineProps<{
  product: GetProductResponse
  pickers: GetPickersResponse
}>()

const emit = defineEmits<{
  (
    e: 'upgrade-accepted',
    pickerFields?: NonNullable<GetPickersResponse['upgrade']>['pickerFields'],
  ): void
}>()

const route = useRoute()
const i18n = useI18n()
const { trackClick } = useTracking()
const experiments = useExperiments()
const { showBar: showStickyBarTop } = useShowStickyBarTop()
const { showBar: showStickyBarTopSmall } = useShowStickyBarTopSmall()

const applyAnimation = ref(false)
const isLoading = ref(false)
const canUpgrade = ref(false)
const isUpgraded = ref(false)
let timeoutUpgrade = null as ReturnType<typeof setTimeout> | null
let timeoutPosition = null as ReturnType<typeof setTimeout> | null

const isLoadingLabelClassnames = tw`bg-[linear-gradient(90deg,#5b5d62,#4055bf,#5b5d62)] bg-clip-text text-transparent bg-[length:200%_auto]`

const isUpgradedLabelClassnames = tw`overflow-hidden after:absolute after:top-0 after:left-0 after:h-full after:w-full after:-translate-x-full after:content-['']`

const shouldDisplayUpgrade = computed(() => {
  return (
    (isUpgraded.value || props.pickers.upgrade) &&
    experiments['experiment.ppUpgradeForLess'] === 'withUpgrade' &&
    CATEGORIES_WITH_UPGRADE.includes(props.product.tracking.categoryId)
  )
})

const label = computed(() => {
  if (isLoading.value) {
    return i18n(translations.loading)
  }
  if (isUpgraded.value) {
    return i18n(translations.upgraded)
  }

  if (canUpgrade.value) {
    return i18n(translations.canUpgrade)
  }

  return ''
})

const comp = computed(() => {
  return isLoading.value ? 'div' : 'button'
})

const topPosition = computed(() => {
  const stickyBarTop = document.querySelector('[id="sticky-bar-top"]')
  const stickyBarTopSmall = document.querySelector(
    '[id="sticky-bar-top-small"]',
  )

  if (showStickyBarTop.value && stickyBarTop) {
    return { top: `${stickyBarTop.clientHeight + 8}px` }
  }

  if (
    showStickyBarTopSmall.value &&
    stickyBarTopSmall &&
    'checkVisibility' in stickyBarTopSmall &&
    stickyBarTopSmall.checkVisibility()
  ) {
    return { top: `${stickyBarTopSmall.clientHeight + 8}px` }
  }

  return undefined
})

async function handleUpgradeAccepted() {
  canUpgrade.value = false
  isUpgraded.value = true

  emit('upgrade-accepted', props.pickers.upgrade?.pickerFields)

  await navigateTo({
    path: route.path,
    query: {
      ...route.query,
      l: props.pickers.upgrade?.grade,
      offerType: props.pickers.upgrade?.specialOfferType,
    },
  })
}

function handlePillClick() {
  if (isLoading.value) {
    return
  }

  if (canUpgrade.value) {
    openModal(MODAL_NAMES.UPGRADE_FOR_LESS)
    trackClick({
      zone: 'upgrade_for_less_modal',
      value: 'open_modal',
    })
  }

  if (isUpgraded.value) {
    trackClick({
      zone: 'upgrade_for_less_modal',
      value: 'smart_upgrade_applied',
    })
  }
}

onBeforeUnmount(() => {
  if (timeoutUpgrade) {
    clearTimeout(timeoutUpgrade)
  }
  if (timeoutPosition) {
    clearTimeout(timeoutPosition)
  }
})

watch(
  topPosition,
  () => {
    applyAnimation.value = true

    timeoutPosition = setTimeout(() => {
      applyAnimation.value = false
    }, 1000)
  },
  { immediate: true },
)

watch(
  () => props.pickers,
  (newValue, oldValue) => {
    if (timeoutUpgrade) {
      clearTimeout(timeoutUpgrade)
    }

    // Detects if the user has upgraded the offer
    if (!newValue?.upgrade && oldValue?.upgrade && isUpgraded.value) {
      return
    }
    // Detects first load
    if (!oldValue && !newValue?.upgrade) {
      return
    }
    isLoading.value = true
    canUpgrade.value = false
    isUpgraded.value = false
    timeoutUpgrade = setTimeout(() => {
      isLoading.value = false
      if (props.pickers.upgrade !== null) {
        canUpgrade.value = true
      }
    }, 2300)
  },
  { immediate: true },
)
</script>

<style module lang="css" scoped>
.slideInFromTop {
  animation: slideDown 500ms ease-out forwards;
}

@keyframes slideDown {
  from {
    transform: translateX(-50%) translateY(-100px);
  }
  to {
    transform: translateX(-50%) translateY(0);
  }
}

.animateShimmer::after {
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 80%
  );
  animation: shimmer 2s 2s forwards;
}

@keyframes shimmer {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(100%);
  }
}

.animateRainbow {
  animation: rainbow 1s linear infinite;
}

@keyframes rainbow {
  from {
    background-position: 200% center;
  }
  to {
    background-position: 0% center;
  }
}
</style>
